import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { UserIdsTokenService } from 'src/app/services/user-ids-token/user-ids-token.service';
import { CleanExpiredTokens, GetUserIdsToken, SetUserIdsToken, UserIdsTokenReset } from './actions';
import { CachedUserIdsToken, UserIdsTokenModel } from './model';

/**
 * Create a hash from userIds array to use as a key for cached tokens
 */

const userIdsHashCache = new Map<string, string>();

function createUserIdsHash(userIds: string[], customHash?: string): string {
  // If customHash is provided, use it instead of generating one
  if (customHash) {
    return customHash;
  }

  // Generate a cache key by joining the original array (unsorted)
  const cacheKey = userIds.join(',');

  // Check if the hash is already cached
  if (userIdsHashCache.has(cacheKey)) {
    return userIdsHashCache.get(cacheKey);
  }

  // Otherwise, sort to ensure same array in different order produces the same hash
  const hash = userIds.slice().sort().join(',');

  // Cache the computed hash
  userIdsHashCache.set(cacheKey, hash);

  return hash;
}

/**
 * Milliseconds in one day
 */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

@State<UserIdsTokenModel>({
  name: 'userIdsToken',
  defaults: {
    cachedTokens: {},
  },
})
@Injectable()
export class UserIdsTokenState {
  constructor(
    private userIdsTokenService: UserIdsTokenService,
    private store: Store,
  ) {}

  /**
   * Selector for all cached tokens
   */
  @Selector()
  static cachedTokens(state: UserIdsTokenModel): Record<string, CachedUserIdsToken> {
    return state.cachedTokens;
  }

  /**
   * Get a token for a specific userIds array if it exists and is not expired
   */
  @Selector()
  static getToken(state: UserIdsTokenModel) {
    return (userIds: string[], companyId: string, customHash?: string): string | null => {
      if (!state || !state.cachedTokens) {
        return null;
      }

      const hash = createUserIdsHash(userIds, customHash);
      const cachedToken = state.cachedTokens[hash];

      // Check if token exists, is for the same company, and hasn't expired
      if (
        cachedToken &&
        cachedToken.companyId === companyId &&
        cachedToken.expiresAt > Date.now()
      ) {
        return cachedToken.token;
      }

      return null;
    };
  }

  /**
   * Reset the token store
   */
  @Action(UserIdsTokenReset)
  reset(ctx: StateContext<UserIdsTokenModel>) {
    ctx.setState({
      cachedTokens: {},
    });
  }

  /**
   * Set a new token in the store with expiration date
   * Keeps only the 10 most recent tokens
   */
  @Action(SetUserIdsToken)
  setToken(ctx: StateContext<UserIdsTokenModel>, { userIds, companyId, token, customHash }: SetUserIdsToken) {
    const hash = createUserIdsHash(userIds, customHash);
    const now = Date.now();

    const state = ctx.getState();
    const newToken = {
      token,
      companyId,
      expiresAt: now + ONE_DAY_MS, // Token expires in 1 day
      timestamp: now,
    };

    // Add the new token to the cache
    const updatedCache = {
      ...state.cachedTokens,
      [hash]: newToken,
    };

    // If we have more than 10 entries, remove the oldest ones
    if (Object.keys(updatedCache).length > 10) {
      // Sort entries by timestamp (oldest first)
      const entries = Object.entries(updatedCache)
        .sort(([, a], [, b]) => a.timestamp - b.timestamp);

      // Keep only the 10 newest entries
      const newestEntries = entries.slice(-10);

      // Convert back to an object
      const limitedCache = Object.fromEntries(newestEntries);

      ctx.setState({
        ...state,
        cachedTokens: limitedCache,
      });
    } else {
      ctx.setState({
        ...state,
        cachedTokens: updatedCache,
      });
    }
  }

  /**
   * Get a token, either from cache or by fetching a new one
   */
  @Action(GetUserIdsToken)
  getToken(ctx: StateContext<UserIdsTokenModel>, { userIds, companyId, customHash }: GetUserIdsToken): Observable<string> {
    // Try to get from cache first
    const cachedToken = UserIdsTokenState.getToken(ctx.getState())(userIds, companyId, customHash);

    if (cachedToken) {
      return of(cachedToken);
    }

    // Not in cache or expired, get a new token
    return this.userIdsTokenService.getUserIdsToken(userIds, companyId).pipe(
      tap(response => {
        ctx.dispatch(new SetUserIdsToken(userIds, companyId, response.userIdsToken, customHash));
      }),
      map(response => response.userIdsToken),
      catchError(error => {
        console.error('Error getting userIdsToken:', error);
        return of('ERROR');
      }),
    );
  }

  /**
   * Remove all expired tokens from cache
   */
  @Action(CleanExpiredTokens)
  cleanExpiredTokens(ctx: StateContext<UserIdsTokenModel>) {
    const state = ctx.getState();
    const currentTime = Date.now();
    const updatedTokens = { ...state.cachedTokens };

    Object.keys(updatedTokens).forEach(hash => {
      if (updatedTokens[hash].expiresAt <= currentTime) {
        delete updatedTokens[hash];
      }
    });

    ctx.setState({
      ...state,
      cachedTokens: updatedTokens,
    });
  }
}
